import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaDownload } from 'react-icons/fa';
import Logo from './Logo';
import usePWAInstall from '../hooks/usePWAInstall';
import IOSInstallModal from './IOSInstallModal';
import './Navigation.css';

const Navigation = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showIOSModal, setShowIOSModal] = useState(false);
  const location = useLocation();
  const { isInstallable, isIOS, installApp } = usePWAInstall();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // UseEffect to lock body scroll when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [menuOpen]);

  return (
    <motion.nav
      // Remove 'scrolled' class when menu is open to prevent backdrop-filter from breaking fixed positioning
      className={`navigation ${scrolled && !menuOpen ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      style={{ zIndex: 1000 }} // Ensure base z-index is set
    >
      <div className="nav-container">
        <Link to="/" className="logo-link">
          <Logo />
        </Link>

        <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
          {[
            { to: '/', label: 'Home' },
            { to: '/products', label: 'Products' },
            { to: '/contact', label: 'Contact' },
            { to: '/careers', label: 'Careers' },
            { to: '/company-info', label: 'Company Info' }
          ].map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`nav-link ${location.pathname === link.to ? 'active' : ''}`}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
              {location.pathname === link.to && (
                <motion.div className="active-dot" layoutId="activeDot" />
              )}
            </Link>
          ))}
          {(isInstallable || isIOS) && (
            <button 
              className="nav-install-btn" 
              onClick={() => {
                if (isIOS) {
                  setShowIOSModal(true);
                } else {
                  installApp();
                }
                setMenuOpen(false);
              }}
              aria-label="Install App"
            >
              <FaDownload className="install-icon" />
              <span>Install App</span>
            </button>
          )}
        </div>

        <button
          className={`hamburger ${menuOpen ? 'active' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>
      </div>

      <IOSInstallModal isOpen={showIOSModal} onClose={() => setShowIOSModal(false)} />
    </motion.nav>
  );
};

export default Navigation;